import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, ChevronLeft, ChevronRight, FileUp, Loader2, RefreshCw, ScanLine, XCircle } from 'lucide-react';
import { api } from '../api';
import { useAvisos } from '../lib/contextoAvisos';
import Ayuda from '../ui/Ayuda';
import EstadoVacio from '../ui/EstadoVacio';
import ModalConfirmar from '../ui/ModalConfirmar';
import {
  CONFIANZA, ESTADO, POR_PAGINA, SIN_RED_AVISO, SIN_RED_MOTIVO, avisoAltas, avisoDecision, avisoLeidas, avisoYaGuardadas,
  consecuencia, cuentaDeFilas, cuentasDelServidor, duplicadasPorDecidir, motivoAprobarAltas, motivoGuardar,
  paresDuplicados, queSeHaria, repetidasSinPar,
} from './migracion';

/* =====================================================================
   «Revisa lo que entendió TEGO» · U-04 fase a1 (DISEÑO-U-04 §6.2)
   =====================================================================
   Cada fila del archivo, con lo que TEGO haría y qué tan seguro está.
   Nada entra al inventario hasta que se aprueba y se pulsa Guardar.

   En a1 la ruta `migracion` abría esta pantalla directamente; desde a2 la
   abre el asistente de seis pasos (`MigracionAsistente`). Si no hay
   migración abierta, la abre al subir el primer archivo («Empezar» es
   idempotente en el servidor).

   LAS CIFRAS SON DEL SERVIDOR (H2 de la auditoría a1). La lista pinta una
   página; «Aprobar las N», «Guardar N aprobadas» y la confirmación salen
   de `/resumen`, que cuenta toda la migración. Con 150 filas, contar lo
   pintado decía «Guardar 0 aprobadas» con 10 aprobadas en la página 2.

   EL BOTÓN PRIMARIO VA ABAJO Y EN EL FLUJO, NO FLOTANDO. El diseño lo pide
   «fijo»; un flotante a 320 px tapa el último botón de la lista (M6, la
   guarda móvil de la casa lo cuenta como P0). Queda al final de la lista,
   a todo lo ancho y con su motivo visible cuando no se puede pulsar.
   ===================================================================== */

const BOTON = 'min-h-11 rounded-xl px-3 text-sm font-semibold disabled:opacity-50';
const PRIMARIO = `${BOTON} w-full bg-[var(--color-marca)] text-white`;
const SECUNDARIO = `${BOTON} border border-gray-300 bg-white text-gray-800`;

function Chip({ confianza, frase }) {
  const c = CONFIANZA[confianza] || CONFIANZA.BAJA;
  return (
    <div className="flex flex-col gap-1" data-confianza={confianza}>
      <span className={`inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${c.clase}`}>
        Confianza {c.palabra}
      </span>
      <span className="text-xs text-gray-700 break-words">{frase}</span>
    </div>
  );
}

function Corregir({ fila, onGuardar, onCerrar }) {
  const it = fila.interpretacion || {};
  const [inicial] = useState(() => ({
    sku: it.sku || '', nombre: it.nombre || '', cantidad: it.cantidad ?? '',
    ubicacion: it.ubicacion || '', lote: it.lote || '',
  }));
  const [v, setV] = useState(inicial);
  // N7 de la auditoría a1: solo viaja lo que la persona cambió. Mandar los
  // cinco marcaba todos como corregidos a mano y apagaba avisos de campos
  // que nadie tocó.
  const cambiados = Object.fromEntries(Object.entries(v).filter(([k, x]) => x !== inicial[k]));
  const cantidadMal = v.cantidad !== '' && !(Number(v.cantidad) > 0);
  const cambiar = (campo) => (e) => {
    const valor = campo === 'sku' || campo === 'ubicacion'
      ? e.target.value.toUpperCase().replace(/\s+/g, '') : e.target.value;
    setV({ ...v, [campo]: valor });
  };
  return (
    <form className="mt-2 flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (cantidadMal) return;
            if (Object.keys(cambiados).length === 0) onCerrar(); else onGuardar(cambiados);
          }}>
      <label className="text-sm">SKU
        <input className="entrada" value={v.sku} onChange={cambiar('sku')} />
      </label>
      {it.sku_propuesto && <Ayuda id="migracion.sku_propuesto" />}
      <label className="text-sm">Nombre
        <input className="entrada" value={v.nombre} onChange={cambiar('nombre')} />
      </label>
      <label className="text-sm">Cantidad
        <input className="entrada" inputMode="decimal" value={v.cantidad} onChange={cambiar('cantidad')} />
      </label>
      {cantidadMal && (
        <p className="text-xs text-red-700">La cantidad tiene que ser mayor que cero. Si no hay piezas, rechaza la fila.</p>
      )}
      <label className="text-sm">Ubicación (código)
        <input className="entrada" value={v.ubicacion} onChange={cambiar('ubicacion')} />
      </label>
      <label className="text-sm">Lote
        <input className="entrada" value={v.lote} onChange={cambiar('lote')} />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className={SECUNDARIO} onClick={onCerrar}>Cancelar</button>
        <button type="submit" className={`${BOTON} bg-[var(--color-marca)] text-white`} disabled={cantidadMal}>
          Guardar corrección
        </button>
      </div>
    </form>
  );
}

function Fila({ fila, ocupado, enLinea, onDecidir, onCorregir }) {
  const [corrigiendo, setCorrigiendo] = useState(false);
  const aplicada = fila.estado === 'APLICADA';
  const bloqueo = !enLinea || ocupado || aplicada;
  return (
    <li className="rounded-xl border border-gray-200 bg-white p-3" data-fila={fila.id}>
      <div className="text-xs text-gray-500 break-words">
        Fila {fila.numero}{fila.lote?.nombre_archivo ? ` · ${fila.lote.nombre_archivo}` : ''} · {fila.texto_crudo}
      </div>
      <div className="mt-1 font-medium text-gray-900 break-words">{queSeHaria(fila)}</div>
      <div className="mt-1 text-xs text-gray-600">Estado: {ESTADO[fila.estado] || fila.estado}</div>
      {fila.interpretacion?.devuelta && (
        <p className="mt-1 rounded-lg bg-yellow-50 p-2 text-xs text-yellow-900 break-words">{fila.interpretacion.devuelta}</p>
      )}
      <div className="mt-2"><Chip confianza={fila.confianza} frase={fila.frase} /></div>
      {fila.motivo_bloqueo && (
        <p className="mt-1 text-xs text-red-700 break-words">{fila.motivo_bloqueo}</p>
      )}
      {!aplicada && (
        <div className="mt-2 grid grid-cols-3 gap-2">
          <button type="button" className={SECUNDARIO}
                  disabled={bloqueo || Boolean(fila.motivo_bloqueo) || fila.estado === 'APROBADA'}
                  onClick={() => onDecidir('APROBAR', [fila.id])}>
            <CheckCircle2 className="mr-1 inline h-4 w-4" aria-hidden="true" />Aprobar
          </button>
          <button type="button" className={SECUNDARIO} disabled={bloqueo || fila.estado === 'RECHAZADA'}
                  onClick={() => onDecidir('RECHAZAR', [fila.id])}>
            <XCircle className="mr-1 inline h-4 w-4" aria-hidden="true" />Rechazar
          </button>
          <button type="button" className={SECUNDARIO} disabled={bloqueo}
                  onClick={() => setCorrigiendo(true)}>
            Corregir
          </button>
        </div>
      )}
      {fila.motivo_bloqueo && !aplicada && (
        <p className="mt-1 text-xs text-gray-600">Aprobar: corrige primero lo que dice arriba.</p>
      )}
      {corrigiendo && (
        <Corregir fila={fila} onCerrar={() => setCorrigiendo(false)}
                  onGuardar={async (v) => { await onCorregir(fila, v); setCorrigiendo(false); }} />
      )}
    </li>
  );
}

/* Las páginas de la revisión. No es el `Paginador` de la casa porque sus
   botones miden menos de 44 px y aquí se pagina con el pulgar (M4). Dice
   SIEMPRE cuántas hay: una lista recortada sin decirlo hace creer que ya
   se vio todo. */
function Paginas({ pagina, total, mostrados, ocupado, onPagina }) {
  const desconocido = total === null || total === undefined;
  const hay = desconocido ? mostrados >= POR_PAGINA : total > POR_PAGINA;
  if (!hay && pagina === 0) return null;
  const desde = pagina * POR_PAGINA + 1;
  const hasta = pagina * POR_PAGINA + mostrados;
  const haySiguiente = desconocido ? mostrados >= POR_PAGINA : hasta < total;
  return (
    <nav aria-label="Paginación" className="flex flex-col gap-2">
      <p className="text-sm text-gray-600" aria-live="polite">
        {desconocido ? `Página ${pagina + 1}` : `Mostrando ${desde}–${hasta} de ${total}`}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className={SECUNDARIO} aria-label="Página anterior"
                disabled={pagina === 0 || ocupado} onClick={() => onPagina(pagina - 1)}>
          <ChevronLeft className="mr-1 inline h-4 w-4" aria-hidden="true" />Anteriores
        </button>
        <button type="button" className={SECUNDARIO} aria-label="Página siguiente"
                disabled={!haySiguiente || ocupado} onClick={() => onPagina(pagina + 1)}>
          Siguientes<ChevronRight className="ml-1 inline h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}

// `onCapturar` (a2, opcional): cuando la abre el asistente, el estado vacío
// ofrece también capturar desde el teléfono. Sin él, la pantalla es la de a1.
export default function RevisionFilas({ onCapturar } = {}) {
  const avisos = useAvisos();
  const [migracion, setMigracion] = useState(null);
  const [filas, setFilas] = useState([]);
  const [total, setTotal] = useState(null);
  const [pagina, setPagina] = useState(0);
  const [duplicadas, setDuplicadas] = useState({ filas: [], total: null });
  const [resumen, setResumen] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [devueltas, setDevueltas] = useState([]);
  const [ubicaciones, setUbicaciones] = useState([]);
  const [enLinea, setEnLinea] = useState(typeof navigator === 'undefined' || navigator.onLine !== false);
  const entrada = useRef(null);

  useEffect(() => {
    const on = () => setEnLinea(true);
    const off = () => setEnLinea(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const cargar = useCallback(async () => {
    setError('');
    try {
      api.get('/ubicaciones').then((u) => setUbicaciones(Array.isArray(u) ? u : []))
        .catch(() => setUbicaciones([]));
      const actual = await api.get('/migracion/actual');
      const m = actual?.migracion || null;
      setMigracion(m);
      if (m) {
        const consulta = `por_pagina=${POR_PAGINA}&pagina=${pagina + 1}`
          + (filtro ? `&confianza=${filtro}` : '');
        const [lista, dup, r] = await Promise.all([
          api.getConTotal(`/migracion/${m.id}/filas?${consulta}`),
          api.getConTotal(`/migracion/${m.id}/filas?solo_duplicadas=true&por_pagina=100`),
          api.get(`/migracion/${m.id}/resumen`),
        ]);
        setFilas(Array.isArray(lista?.filas) ? lista.filas : []);
        setTotal(lista?.total ?? null);
        setDuplicadas({ filas: Array.isArray(dup?.filas) ? dup.filas : [], total: dup?.total ?? null });
        setResumen(r);
      } else {
        setFilas([]);
        setTotal(null);
        setDuplicadas({ filas: [], total: null });
        setResumen(null);
      }
    } catch (e) {
      setError(e.message || 'No se pudo leer la revisión.');
    } finally {
      setCargando(false);
    }
  }, [pagina, filtro]);

  useEffect(() => { cargar(); }, [cargar]);

  async function asegurarMigracion() {
    if (migracion) return migracion;
    const m = await api.post('/migracion', { forma_actual: 'EXCEL' });
    setMigracion(m);
    return m;
  }

  async function elegirUbicacion(codigo) {
    if (!codigo) return;
    setOcupado(true);
    try {
      const m = migracion
        ? await api.patch(`/migracion/${migracion.id}`, { ubicacion_omision: codigo })
        : await api.post('/migracion', { forma_actual: 'EXCEL', ubicacion_omision: codigo });
      setMigracion(m);
      avisos.exito(`Lo que no diga ubicación irá a ${m.ubicacion_omision?.nombre || codigo}.`);
    } catch (e) {
      avisos.error(e.message);
    } finally {
      setOcupado(false);
    }
  }

  async function subir(archivo) {
    if (!archivo) return;
    setOcupado(true);
    try {
      const m = await asegurarMigracion();
      const r = await api.subir(`/migracion/${m.id}/lotes`, archivo);
      avisos.exito(avisoLeidas(r.totales.filas, r.lote.nombre_archivo));
      await cargar();
    } catch (e) {
      avisos.error(e.message);
    } finally {
      setOcupado(false);
      if (entrada.current) entrada.current.value = '';
    }
  }

  async function decidir(decision, ids, extra = {}) {
    setOcupado(true);
    try {
      const r = await api.post(`/migracion/${migracion.id}/filas/decidir`, { decision, ids, ...extra });
      if (r.omitidas?.length) avisos.aviso(avisoDecision(r));
      else avisos.exito(avisoDecision(r));
      await cargar();
    } catch (e) {
      avisos.error(e.message);
    } finally {
      setOcupado(false);
    }
  }

  async function aprobarAltas() {
    setOcupado(true);
    try {
      const r = await api.post(`/migracion/${migracion.id}/filas/decidir`,
        { decision: 'APROBAR', filtro: { confianza: 'ALTA', estado: 'PENDIENTE' } });
      avisos.exito(avisoAltas(r.cambiadas));
      await cargar();
    } catch (e) {
      avisos.error(e.message);
    } finally {
      setOcupado(false);
    }
  }

  async function corregir(fila, v) {
    try {
      await api.patch(`/migracion/${migracion.id}/filas/${fila.id}`, v);
      avisos.exito(`Fila ${fila.numero} corregida. Vuelve a decidirla.`);
      await cargar();
    } catch (e) {
      avisos.error(e.message);
    }
  }

  async function guardar() {
    const r = await api.post(`/migracion/${migracion.id}/aplicar`, {});
    avisos.exito(r.mensaje);
    setDevueltas(r.devueltas || []);
    setConfirmar(false);
    await cargar();
  }

  const { altas, cuenta, bloqueadas, enTotal } = cuentasDelServidor(resumen);
  const pares = paresDuplicados(duplicadas.filas);
  const porDecidir = duplicadasPorDecidir(duplicadas.filas, resumen);
  // C-b (tercera auditoría a1): las repetidas cuya pareja ya se guardó se
  // cuentan y se deciden aparte, debajo de los pares, sin taparlos.
  const yaGuardadas = Number(resumen?.duplicadas_ya_guardadas || 0);
  const sinPar = porDecidir.faltan ? [] : repetidasSinPar(duplicadas.filas);
  const porConfianza = resumen?.por_confianza || {};
  const elegirFiltro = (c) => { setCargando(true); setPagina(0); setFiltro(filtro === c ? '' : c); };
  const motivoAltas = !enLinea ? SIN_RED_MOTIVO : motivoAprobarAltas(altas);
  const motivoDeGuardar = motivoGuardar(cuenta.filas, enLinea);

  return (
    <div className="flex flex-col gap-4" data-migracion="revision">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Revisa lo que entendió TEGO</h1>
        <p className="text-sm text-gray-600">Nada entra al inventario hasta que lo apruebas y pulsas Guardar.</p>
      </div>
      <Ayuda id="migracion.revision.pantalla" />
      {!enLinea && (
        <p className="rounded-xl bg-yellow-100 p-3 text-sm text-yellow-900">
          {SIN_RED_AVISO}
        </p>
      )}

      <div className="flex flex-col gap-1">
        <label className={`${SECUNDARIO} flex cursor-pointer items-center justify-center`}>
          <FileUp className="mr-1 h-4 w-4" aria-hidden="true" />Subir archivo
          <input ref={entrada} type="file" accept=".xlsx,.csv" className="entrada sr-only"
                 disabled={ocupado || !enLinea} onChange={(e) => subir(e.target.files?.[0])} />
        </label>
        <Ayuda id="migracion.archivo" />
      </div>
      <label className="flex flex-col gap-1 text-sm text-gray-800">
        Dónde está la mercancía
        <select className="entrada" value={migracion?.ubicacion_omision?.codigo || ''}
                disabled={ocupado || !enLinea} onChange={(e) => elegirUbicacion(e.target.value)}>
          <option value="">{ubicaciones.length ? 'Elige una ubicación' : 'Primero da de alta una ubicación en Ubicaciones'}</option>
          {ubicaciones.map((u) => <option key={u.id} value={u.codigo}>{u.nombre} ({u.codigo})</option>)}
        </select>
        <Ayuda id="migracion.ubicacion_omision" />
      </label>
      {migracion && (
        <p className="text-xs text-gray-600">Migración con folio {migracion.folio}.</p>
      )}

      {cargando && (
        <div className="flex flex-col gap-2" aria-busy="true">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Cargando la revisión…
          </div>
          {[1, 2, 3].map((n) => <div key={n} className="h-24 rounded-xl bg-gray-100" />)}
        </div>
      )}

      {!cargando && error && (
        <div className="rounded-xl bg-red-50 p-3 text-sm text-red-800">
          <p>No se pudo leer la revisión: {error}</p>
          <button type="button" className={`${SECUNDARIO} mt-2 w-full`} onClick={() => { setCargando(true); cargar(); }}>
            <RefreshCw className="mr-1 inline h-4 w-4" aria-hidden="true" />Volver a intentarlo
          </button>
        </div>
      )}

      {!cargando && !error && enTotal === 0 && filas.length === 0 && (
        <EstadoVacio icono={FileUp} titulo="Todavía no hay nada que revisar"
          descripcion="Sube tu archivo de Excel o CSV con lo que tienes: una columna con el nombre del producto y otra con cuántas hay."
          accion={onCapturar && (
            <button type="button" className={SECUNDARIO} onClick={onCapturar}>
              <ScanLine className="mr-1 inline h-4 w-4" aria-hidden="true" />O captura desde el teléfono
            </button>
          )} />
      )}

      {!cargando && !error && (enTotal > 0 || filas.length > 0) && (
        <>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar por confianza">
            {['ALTA', 'MEDIA', 'BAJA'].map((c) => (
              <button key={c} type="button" aria-pressed={filtro === c}
                      className={`${BOTON} border ${CONFIANZA[c].clase} ${filtro === c ? 'ring-2 ring-offset-1' : ''}`}
                      onClick={() => elegirFiltro(c)}>
                {CONFIANZA[c].palabra}: {porConfianza[c] ?? 0}
              </button>
            ))}
          </div>
          <Ayuda id="migracion.confianza" />

          <div className="flex flex-col gap-1">
            <button type="button" className={PRIMARIO} disabled={Boolean(motivoAltas) || ocupado}
                    onClick={aprobarAltas}>
              {altas === 1 ? 'Aprobar 1 de confianza alta' : `Aprobar las ${altas} de confianza alta`}
            </button>
            {motivoAltas && <p className="text-xs text-gray-600" data-motivo="aprobar-altas">{motivoAltas}</p>}
            <Ayuda id="migracion.aprobar_altas" />
          </div>

          {(pares.length > 0 || porDecidir.total > 0 || yaGuardadas > 0) && (
            <section className="flex flex-col gap-2" data-seccion="dos-veces">
              <h2 className="font-semibold text-gray-900">Vienen dos veces</h2>
              <Ayuda id="migracion.duplicado" />
              {porDecidir.faltan && (
                <p className="text-xs text-gray-600" data-faltan-pares={porDecidir.total - porDecidir.vistas}>
                  Aquí van {porDecidir.vistas} de las {porDecidir.total} filas repetidas por decidir; al decidirlas
                  aparecen las demás.
                </p>
              )}
              {pares.map((par) => {
                const total = par.reduce((s, f) => s + Number(f.interpretacion?.cantidad || 0), 0);
                return (
                  <div key={par[0].id} className="rounded-xl border border-yellow-300 bg-yellow-50 p-3">
                    {par.map((f) => (
                      <p key={f.id} className="text-sm break-words">Fila {f.numero}: {queSeHaria(f)}</p>
                    ))}
                    <div className="mt-2 grid grid-cols-1 gap-2">
                      <button type="button" className={SECUNDARIO} disabled={ocupado || !enLinea}
                              onClick={() => decidir('APROBAR', par.map((f) => f.id), { sumar_duplicados: true })}>
                        Sumar las dos ({total})
                      </button>
                      <button type="button" className={SECUNDARIO} disabled={ocupado || !enLinea}
                              onClick={() => decidir('RECHAZAR', par.slice(1).map((f) => f.id))}>
                        Quedarme con una
                      </button>
                    </div>
                  </div>
                );
              })}
              {yaGuardadas > 0 && (
                <p className="text-xs text-gray-600" data-ya-guardadas={yaGuardadas}>{avisoYaGuardadas(yaGuardadas)}</p>
              )}
              {sinPar.map((f) => (
                <div key={f.id} className="rounded-xl border border-yellow-300 bg-yellow-50 p-3" data-repetida-sin-par={f.id}>
                  <p className="text-sm break-words">Fila {f.numero}: {queSeHaria(f)}</p>
                  <div className="mt-2 grid grid-cols-1 gap-2">
                    <button type="button" className={SECUNDARIO} disabled={ocupado || !enLinea}
                            onClick={() => decidir('APROBAR', [f.id], { sumar_duplicados: true })}>
                      Sumar a lo guardado
                    </button>
                    <button type="button" className={SECUNDARIO} disabled={ocupado || !enLinea}
                            onClick={() => decidir('RECHAZAR', [f.id])}>
                      Rechazar esta fila
                    </button>
                  </div>
                </div>
              ))}
            </section>
          )}

          {bloqueadas > 0 && (
            <section className="flex flex-col gap-1">
              <h2 className="font-semibold text-gray-900">No se pueden guardar así ({bloqueadas})</h2>
              <Ayuda id="migracion.bloqueada" />
            </section>
          )}

          {filas.length === 0 && filtro && (
            <p className="rounded-xl bg-gray-50 p-3 text-sm text-gray-700">
              No hay filas con confianza {CONFIANZA[filtro].palabra.toLowerCase()}. Pulsa otra vez el filtro para ver todas.
            </p>
          )}
          <ul className="flex flex-col gap-2">
            {filas.map((f) => (
              <Fila key={f.id} fila={f} ocupado={ocupado} enLinea={enLinea}
                    onDecidir={decidir} onCorregir={corregir} />
            ))}
          </ul>
          <Paginas pagina={pagina} total={total} mostrados={filas.length} ocupado={ocupado}
                   onPagina={(n) => { setCargando(true); setPagina(Math.max(0, n)); }} />

          {devueltas.length > 0 && (
            <div className="rounded-xl bg-yellow-100 p-3 text-sm text-yellow-900">
              <p className="font-semibold">Volvieron a revisión:</p>
              {devueltas.map((d) => <p key={d.id} className="break-words">Fila {d.numero}: {d.motivo}</p>)}
            </div>
          )}

          <div className="flex flex-col gap-1">
            <button type="button" className={PRIMARIO} disabled={Boolean(motivoDeGuardar) || ocupado}
                    onClick={() => setConfirmar(true)}>
              Guardar {cuenta.filas} {cuenta.filas === 1 ? 'aprobada' : 'aprobadas'}
            </button>
            {motivoDeGuardar && <p className="text-xs text-gray-600" data-motivo="guardar">{motivoDeGuardar}</p>}
            <Ayuda id="migracion.guardar" />
          </div>
        </>
      )}

      {confirmar && (
        <ModalConfirmar
          titulo={`Guardar ${cuentaDeFilas(cuenta.filas)} en el inventario`}
          descripcion={consecuencia(cuenta)}
          etiquetaConfirmar="Sí, guardar"
          onConfirmar={guardar}
          onCerrar={() => setConfirmar(false)} />
      )}
    </div>
  );
}
